import { Dispatch, Reducer, useEffect, useMemo, useReducer, useRef } from 'react';

export interface Thunk<S, A> {
  (dispatch: ThunkDispatcher<S, A>, getState?: () => S): any;
}

export interface ThunkDispatcher<S, A> {
  <T>(thunk: Thunk<S, A>): T;

  (action: A): void;
}

const isThunk = <S, A>(tbd: any): tbd is Thunk<S, A> => typeof tbd === 'function';

const thunked =
  <S, A>(dispatch: Dispatch<A>, getState: () => S): ThunkDispatcher<S, A> =>
  (action: A | Thunk<S, A>) => {
    if (isThunk<S, A>(action)) {
      return action(thunked(dispatch, getState), getState);
    }
    dispatch(action);
  };

export const useThunkReducer = <S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
): [S, ThunkDispatcher<S, A>] => {
  const [contextState, dispatchContext] = useReducer(reducer, initialState);
  const stateRef = useRef<S>(contextState);

  const dispatcher = useMemo(() => thunked(dispatchContext, () => stateRef.current), []);

  useEffect(() => {
    stateRef.current = contextState;
  });

  return [contextState, dispatcher];
};
